import React, { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Drawer, Flex } from "antd"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useDevice } from "hooks/useDevice"
import { routes } from "router/routes"
import { buildDayBlackoutReservationsLink, buildReservationsLink, openReservationsLink } from "helpers/blackouts"
import { Icon } from "ui/Icon"
import { AVAILABILITY_STATUSES, CONFLICT_HEADINGS, DATE_KEY_FORMAT, DRAWER_DATE_FORMAT, DRAWER_WIDTH } from "../../constants"
import { formatSlotTime, formatTicketedSlot, getBookableSlots, groupReasons, isTicketedListing } from "../../utils"
import useDayReservations from "../../hooks/useDayReservations"
import { useEquipmentTargets } from "pagesV2/pro/Settings/Blackout/hooks/useEquipmentTargets"
import ListingSummaryCard from "./ListingSummaryCard"
import BlackoutSection from "./BlackoutSection"
import DayReservations from "./DayReservations"
import styles from "./index.module.css"

/**
 * One day of a listing's availability, in full.
 *
 * The grid states what a day is; this panel says why, and gives the two ways out — the blackout
 * that closed the day, or the reservations that filled it. The date arrows step day by day rather
 * than closing and reopening, because "why is this week bad" is read one day at a time.
 */
const DayDetailsDrawer = ({ date, day, listing, listingId, isOpen, onClose, onNavigate, onChanged }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const navigate = useNavigate()

  const { blackouts, conflicts } = groupReasons(day?.reasons)
  const { bookings, isLoading } = useDayReservations({ listingId, date, isOpen })
  const equipmentTargets = useEquipmentTargets({ enabled: isOpen })

  const isTicketed = isTicketedListing(listing)
  const slots = getBookableSlots(day, isTicketed)

  const dayReservationsLink = useMemo(
    () => buildReservationsLink({ dateFrom: date, listingIds: listingId ? [listingId] : [] }),
    [date, listingId]
  )

  /** "View Reservations Page" is a departure — the guide is done with the day. */
  const goToReservations = useCallback(() => navigate(dayReservationsLink), [navigate, dayReservationsLink])

  /**
   * "Review" on a blackout card is a look-aside, and its arrow promises a new tab. It has to be
   * one: this panel is a drawer over the calendar, so navigating away would close the day.
   *
   * It follows the blackout rather than this page's listing, because the card's count does: a
   * business closure strands bookings across the whole catalogue, and narrowing the list to the
   * listing being viewed would show none of them under a card claiming there is one.
   */
  const reviewBlackout = useCallback(
    (blackout) => openReservationsLink(buildDayBlackoutReservationsLink(blackout, date, equipmentTargets)),
    [date, equipmentTargets]
  )

  const openBooking = useCallback(
    (booking) => navigate(routes.guideReservationDetailPath({ id: booking.uuid || booking.id })),
    [navigate]
  )

  const shiftDay = (offset) => onNavigate(moment(date, DATE_KEY_FORMAT).add(offset, "days").format(DATE_KEY_FORMAT))

  /**
   * A conflict's card. The feed sends one `detail` line per reason — "Leslie Alexander (Capitan) →
   * Surfing Classes" — which the design splits over two lines: what is taken, and what took it.
   * An `items` array, when the feed sends one, is the same card with a row per resource.
   */
  const renderConflictCard = (reason) => {
    const rows = reason.items?.length
      ? reason.items.map((item) => ({ label: item.label, detail: item.listing }))
      : [reason.detail || ""].map((line) => {
          const [label, detail] = line.split(/\s*(?:→|»|->)\s*/)
          return { label, detail }
        })

    return (
      <div className={styles.conflictCard}>
        {rows.map((row) => (
          <Flex vertical key={`${row.label}-${row.detail}`} className={styles.conflictRow}>
            <span className={styles.conflictLabel}>{row.label}</span>
            {row.detail && <span className={styles.conflictDetail}>{row.detail}</span>}
          </Flex>
        ))}
      </div>
    )
  }

  const slotsHeading =
    day?.status === AVAILABILITY_STATUSES.AVAILABLE
      ? t("availability.available_slots", { defaultValue: "Available slots:" })
      : t("availability.available_start_times", { defaultValue: "Available start times" })

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      placement={isMobile ? "bottom" : "right"}
      width={DRAWER_WIDTH}
      height={isMobile ? "85%" : undefined}
      title={null}
      closable={false}
      classNames={{ body: styles.body }}
    >
      <Flex align="center" justify="flex-end">
        <button type="button" className={styles.iconButton} onClick={onClose} aria-label={t("common.close", { defaultValue: "Close" })}>
          <Icon name="X" width={20} height={20} color="var(--color-text-primary)" />
        </button>
      </Flex>

      <Flex align="center" justify="space-between" gap="var(--spacing-m)" className={styles.dateNav}>
        <button
          type="button"
          className={styles.navButton}
          onClick={() => shiftDay(-1)}
          aria-label={t("availability.previous_day", { defaultValue: "Previous day" })}
        >
          <Icon name="ChevronLeft" width={16} height={16} color="var(--color-text-primary)" />
        </button>
        <span className={styles.dateTitle}>{moment(date, DATE_KEY_FORMAT).format(DRAWER_DATE_FORMAT)}</span>
        <button
          type="button"
          className={styles.navButton}
          onClick={() => shiftDay(1)}
          aria-label={t("availability.next_day", { defaultValue: "Next day" })}
        >
          <Icon name="ChevronRight" width={16} height={16} color="var(--color-text-primary)" />
        </button>
      </Flex>

      <ListingSummaryCard listing={listing} day={day} />

      {/* Stepping the arrows can land on a day the grid has not been given yet — while the next
          month loads, and permanently for a feed that skipped it. */}
      {!day && (
        <span className={styles.emptyText}>
          {t("availability.no_day_information", { defaultValue: "No availability information for this day" })}
        </span>
      )}

      <Flex vertical gap="var(--spacing-l)" className={styles.sections}>
        {blackouts.length > 0 && (
          <BlackoutSection reasons={blackouts} date={date} onReview={reviewBlackout} onChanged={onChanged} />
        )}

        {/* Only the two shared-resource reasons get a section: `fully_booked` says nothing the
            reservation list below does not already say, which is why the design omits it. */}
        {conflicts
          .filter((reason) => CONFLICT_HEADINGS[reason.type])
          .map((reason, index) => (
            <Flex vertical gap="var(--spacing-s)" key={`${reason.type}-${index}`}>
              <span className={styles.sectionHeading}>
                {t(CONFLICT_HEADINGS[reason.type].key, { defaultValue: CONFLICT_HEADINGS[reason.type].fallback })}
              </span>
              {renderConflictCard(reason)}
            </Flex>
          ))}

        {slots.length > 0 && (
          <Flex vertical gap="var(--spacing-s)">
            <span className={styles.sectionHeading}>{slotsHeading}</span>
            <Flex gap="var(--spacing-xxs)" wrap>
              {slots.map((slot) => (
                <span key={slot.time} className={styles.slotPill}>
                  {isTicketed ? formatTicketedSlot(slot, t) : formatSlotTime(slot.time)}
                </span>
              ))}
            </Flex>
          </Flex>
        )}

        <DayReservations bookings={bookings} isLoading={isLoading} onOpenBooking={openBooking} onViewAll={goToReservations} />
      </Flex>
    </Drawer>
  )
}

DayDetailsDrawer.propTypes = {
  /** The day being shown, "YYYY-MM-DD". */
  date: PropTypes.string,
  /** That day's entry from the availability feed, or null when the feed does not describe it. */
  day: PropTypes.shape({
    status: PropTypes.string,
    slots: PropTypes.array,
    reasons: PropTypes.array,
    bookings_count: PropTypes.number
  }),
  listing: PropTypes.shape({ title: PropTypes.string, listing_type: PropTypes.string }),
  /** The picked listing's id — the panel's reservation query and deep link are scoped to it. */
  listingId: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Moves the panel to another date, "YYYY-MM-DD". */
  onNavigate: PropTypes.func.isRequired,
  /** Called after a blackout is saved or deleted so the grid can refetch. */
  onChanged: PropTypes.func
}

export default DayDetailsDrawer
